// src/pages/Dashboard/Admin/PagesPersonnalisees/PagesDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { usePagesDashboard } from "./hooks/usePagesDashboard";

import DashboardPagesHeader from "./components/DashboardPagesHeader";
import PagesKpiCards from "./components/PagesKpiCards";
import PagesFiltersCard from "./components/PagesFiltersCard";
import PagesTable from "./components/PagesTable";
import PageFormDrawer from "./components/PageFormDrawer";
import PageArticlesDrawer from "./components/PageArticlesDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedPagesPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Pages personnalisées
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PagesDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const dashboard = usePagesDashboard();

  if (roleId !== 1) {
    return <UnauthorizedPagesPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardPagesHeader
          loading={dashboard.loading}
          onCreate={dashboard.openCreateDrawer}
          onRefresh={dashboard.fetchPages}
        />

        <PagesKpiCards
          stats={dashboard.stats}
          filteredStats={dashboard.filteredStats}
          loading={dashboard.loading}
        />

        <PagesFiltersCard
          filters={dashboard.filters}
          setFilter={dashboard.setFilter}
          resetFilters={dashboard.resetFilters}
          filteredCount={dashboard.filteredPages.length}
          totalCount={dashboard.pages.length}
          loading={dashboard.loading}
          onRefresh={dashboard.fetchPages}
        />

        <PagesTable
          pages={dashboard.filteredPages}
          loading={dashboard.loading}
          deletingId={dashboard.deletingId}
          onEdit={dashboard.openEditDrawer}
          onDelete={dashboard.handleDeletePage}
          onManageArticles={dashboard.openArticlesDrawer}
        />
      </div>

      <PageFormDrawer
        isOpen={dashboard.formDrawerOpen}
        mode={dashboard.formMode}
        page={dashboard.editablePage}
        submitting={dashboard.submittingForm}
        onClose={dashboard.closeFormDrawer}
        onSubmit={
          dashboard.formMode === "create"
            ? dashboard.handleCreatePage
            : dashboard.handleUpdatePage
        }
      />

      <PageArticlesDrawer
        isOpen={dashboard.articlesDrawerOpen}
        page={dashboard.selectedPage}
        onClose={dashboard.closeArticlesDrawer}
        onUpdated={dashboard.fetchPages}
      />

      <ToastContainer />
    </section>
  );
}
